import React from 'react';
import { Timer } from 'lucide-react';
import {
  formatCurrency,
  formatCurrencyPrecise,
  discountPercent,
  priceBreakdown,
} from '../lib/format';

/**
 * The discount, said out loud, above the checkout breakdown.
 *
 * It appears only when the admin has actually set one — `originalPrice` above
 * `price` in the Workshops tab, which is the same test the hero card and the
 * admin list already use. With the two equal (the live config today) this
 * renders nothing at all, so the checkout card goes back to being the plain
 * fee/GST/total it was.
 *
 * ⚠ Every figure here is derived from the workshop, never written down. The
 *   reference design for this block had a round ₹100 in it; hardcoding that,
 *   or any headline number that is not what the visitor is about to be charged,
 *   is the one thing this must not do — the payment gateway manually reviews this site
 *   for merchant activation, and a price on the page that disagrees with the
 *   amount handed to the gateway is exactly what that review looks for.
 *
 * So the strike-through is the admin's own `originalPrice` and the big figure
 * is the fee — the same pair the hero shows, so the two places on the page
 * cannot contradict each other — and the GST-inclusive total the button
 * actually charges is spelled out underneath rather than left implied.
 */
export const LimitedTimeOffer = ({ workshop }) => {
  const off = discountPercent(workshop.originalPrice, workshop.price);
  if (off <= 0) return null;

  const saving = Number(workshop.originalPrice) - Number(workshop.price);
  const { total } = priceBreakdown(workshop.price, 0);

  return (
    <div className="mb-5 pb-5 border-b border-white/10 text-center">
      <span className="pill bg-brand-500/15 text-brand-300 border border-brand-400/25">
        <Timer className="w-3.5 h-3.5" />
        Limited time
      </span>

      <p className="mt-3 text-sm text-white/70">
        For a limited time, get access for
      </p>

      <p className="mt-1.5 flex items-baseline justify-center gap-3">
        <span
          className="font-sans text-5xl font-extrabold tracking-[-0.035em] text-gold-300"
          data-numeric
        >
          {formatCurrency(workshop.price)}
        </span>
        <span className="text-lg text-white/35 line-through" data-numeric>
          {formatCurrency(workshop.originalPrice)}
        </span>
      </p>

      <p className="mt-2.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-emerald-300">
        You save {formatCurrency(saving)} · {off}% off
      </p>

      {/* The fee is the figure being compared, so the amount that actually
          leaves their account is said here rather than left to the reader to
          work out from the GST line below. */}
      <p className="mt-2 text-[11px] text-white/40">
        Plus 18% GST — you pay {formatCurrencyPrecise(total)} today
      </p>
    </div>
  );
};
